import { useEffect, useLayoutEffect } from 'react'

/**
 * 문서가 밀리는 것을 막는다. 키보드가 올라와도 보던 화면이 그대로 있게 한다.
 *
 * 쓰는 자리
 * --------
 * 문서 자체는 스크롤하지 않고, 높이가 고정된 칸 안에서만 내용이 움직이는 화면.
 * 자세히보기(ReviewDetail)가 먼저 쓰던 방식을 그대로 뽑아낸 것이다.
 * useKeyboardInset 과 짝으로 쓴다 — 이쪽이 '안 밀리게', 그쪽이 '가린 만큼 비우게'.
 *
 * 세 가지를 한다
 * -------------
 * ① 들어올 때 이미 밀려 있으면 그리기 전에 지운다(useLayoutEffect).
 *    그린 뒤에 지우면 한 번 밀렸다 돌아오는 게 눈에 보인다.
 * ② 밀림이 생기는 즉시 지운다. 웹킷이 입력칸을 보여주려고 문서를 밀지만,
 *    이 화면에는 필요 없는 움직임이다. 실기기에서 재보면 웹킷도 32ms 뒤에
 *    스스로 0으로 되돌린다 — 어차피 되돌릴 값이라 곧바로 지워도 부딪히지 않는다.
 * ③ 머무는 동안 문서 스크롤을 잠근다. 브라우저가 밀 여지를 없애면
 *    앱의 웹뷰 축소와 브라우저의 스크롤이 이중으로 보정되는 일이 사라진다.
 *
 *    자세히보기와 같은 잠금을 쓴다(overflow:hidden). position:fixed 로 더 세게
 *    박아 봤지만 밀림은 그대로였다 — 원인이 문서 스크롤이 아니라 **입력칸을 직접
 *    누른 것** 이었기 때문이다. 그쪽은 화면에서 FocusShield 로 막는다.
 */
export function useNoDocScrollPush(): void {
  useLayoutEffect(() => {
    if (window.scrollY !== 0) window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const undo = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0)
    }
    const viewport = window.visualViewport
    window.addEventListener('scroll', undo, { passive: true })
    viewport?.addEventListener('scroll', undo)
    viewport?.addEventListener('resize', undo)
    return () => {
      window.removeEventListener('scroll', undo)
      viewport?.removeEventListener('scroll', undo)
      viewport?.removeEventListener('resize', undo)
    }
  }, [])

  useLayoutEffect(() => {
    document.documentElement.classList.add('lock-doc-scroll')
    return () => {
      document.documentElement.classList.remove('lock-doc-scroll')
    }
  }, [])
}
